import type { CSSProperties, ReactNode } from 'react';

/** Палитра колод (bp-deck2.js): обводка E, акцент A, лицо F1/F2, свечение. */
const DECK = [
  { e: '#7F848C', a: '#B4B9C2', f1: '#8E939B', f2: '#4E535B', glow: 'none' },
  { e: '#5B7196', a: '#9FB4D8', f1: '#6F86AE', f2: '#2E3F5E', glow: 'none' },
  { e: '#7C6B99', a: '#B8A7D6', f1: '#8F7DB0', f2: '#3F3454', glow: '0 0 18px rgba(124,107,153,.55)' },
  { e: '#C8A04E', a: '#F3D98B', f1: '#E1B85C', f2: '#8C6A28', glow: '0 0 22px rgba(200,160,78,.6)' },
] as const;
const SUITS = ['♠', '♥', '♣', '♦'] as const;
const RS = ['ОБЫЧНАЯ', 'РЕДКАЯ', 'ЭПИК', 'ЛЕГЕНДА'] as const;
/** Углы веера (1g): четыре нижние карты, верхняя 0°. */
const FAN = [-28, -14, 14, 28] as const;

function rgba(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${n >> 16}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export type DeckStage = 'idle' | 'open' | 'flip';

/**
 * Колода «Бандероль» + анимация «Веер» (handoff 5, варианты 1b/1g) —
 * порт прототипа `<bp-deck2 design="band" anim="fan">`: открытая стопка из
 * 5 карт, перехваченная бумажной лентой с восковой печатью. Стадии:
 * idle (покачивание ±2,2° + блик) → open (печать 320 мс, ленты 520/200,
 * веер из 5 карт с 600 мс, stagger 55 мс) → flip (4 карты уходят 420 мс,
 * верхняя 760 мс rotateY ×{flipScale}). Лицо карты — `face`, цвета лица —
 * редкость ПРЕДМЕТА (faceRarity).
 */
export function DeckBox({
  rarity,
  stage,
  size = 150,
  flipScale = 1.7,
  faceRarity,
  face,
}: {
  rarity: number;
  stage: DeckStage;
  size?: number;
  flipScale?: number;
  faceRarity?: number;
  face?: ReactNode;
}) {
  const T = DECK[rarity] ?? DECK[1];
  const F = DECK[faceRarity ?? rarity] ?? T;
  const sc = size / 100;
  const vars = {
    '--dk-e': T.e,
    '--dk-a': T.a,
    '--dk-e5': rgba(T.e, 0.5),
    '--dk-lat': rgba(T.e, 0.28),
    '--dk-glow': T.glow,
    '--dk-face-a': F.a,
    '--dk-face-f1': F.f1,
    '--dk-face-f2': F.f2,
    '--dk-face-glow': F.glow === 'none' ? '0 0 14px ' + rgba(F.e, 0.45) : F.glow,
    '--dk-flip': String(flipScale),
  } as CSSProperties;
  const card = (i: number) => (
    <span
      key={i}
      className={`db2-c${i === 4 ? ' db2-c--top' : ''}`}
      style={
        {
          left: i === 4 ? 0 : (4 - i) * 0.9,
          top: i === 4 ? 0 : (4 - i) * 1.1,
          '--ang': `${i === 4 ? 0 : FAN[i]}deg`,
          '--st': `${i * 55}ms`,
        } as CSSProperties
      }
    >
      <span className="db2-cb mono">S</span>
      <span className="db2-cf">
        {i === 4 && face ? (
          face
        ) : (
          <>
            <b>{SUITS[i === 4 ? (faceRarity ?? rarity) : rarity] ?? '♠'}</b>
            {i === 4 && (
              <span className="db2-pips">
                {Array.from({ length: (faceRarity ?? rarity) + 1 }, (_, k) => (
                  <i key={k} />
                ))}
              </span>
            )}
          </>
        )}
      </span>
    </span>
  );
  return (
    <span
      className={`db2 db2--${stage}${size < 44 ? ' db2--mini' : ''}`}
      style={{ width: size, height: Math.round(160 * sc), ...vars }}
      aria-hidden
    >
      <span className="db2-st" style={{ transform: `scale(${sc})` }}>
        <span className="db2-sw">
          {/* толщина стопки */}
          <span className="db2-thk" style={{ left: 25, top: 71 }} />
          <span className="db2-thk" style={{ left: 23, top: 68.5 }} />
          {/* стопка: 4 нижние + верхняя (награда) */}
          <span className="db2-cs">{[0, 1, 2, 3, 4].map(card)}</span>
          {/* бумажная лента с печатью */}
          <span className="db2-band db2-band--l">
            STRADDLE
            <i style={{ left: 30 }} />
          </span>
          <span className="db2-band db2-band--r">
            <i style={{ left: 5 }} />
            {RS[rarity] ?? RS[1]}
          </span>
          <span className="db2-wax">♠</span>
          {/* блик по стопке */}
          <span className="db2-sh">
            <i />
          </span>
        </span>
      </span>
    </span>
  );
}
